import { uploadFile, uploadVideo, isVideoFile } from '@ergeon/file-storage';

import { LEVEL } from '../../../../envs';

type Attachment = {
  dataURL?: string;
  file?: File;
};

type UploadResult = {
  paths: string[];
  urls: string[];
};

const S3_URL_SEPARATOR = '.s3.us-west-2.amazonaws.com/';

/**
 * Extracts the S3 path from a full S3 URL.
 */
const extractPathFromUrl = (url: string): string => {
  const [, path] = decodeURI(url).split(S3_URL_SEPARATOR);
  return path || '';
};

/**
 * Uploads attachments to S3 and returns the paths and URLs.
 * Uses the standard /upload endpoint (consistent with other Ergeon apps).
 * @param attachments - Array of attachments with file data
 */
export const uploadAttachments = async(
  attachments: Attachment[],
): Promise<UploadResult> => {
  const paths: string[] = [];
  const urls: string[] = [];
  const environment = LEVEL === 'production' ? 'production' : 'development';

  for (const attachment of attachments) {
    if (!attachment.file) continue;

    let publicUrl: string | void;

    if (isVideoFile(attachment.file)) {
      publicUrl = await uploadVideo(attachment.file, environment);
    } else {
      publicUrl = await uploadFile(attachment.file, undefined, environment);
    }

    if (publicUrl) {
      paths.push(extractPathFromUrl(publicUrl));
      urls.push(publicUrl);
    }
  }

  return { paths, urls };
};

/**
 * Builds a message string with attachment links appended.
 * @param text - The main message text
 * @param urls - Array of attachment URLs to append
 * @param prefix - Optional prefix to add before the message (e.g., '[SMS]')
 */
export const buildMessageWithAttachments = (
  text: string,
  urls: string[],
  prefix?: string,
): string => {
  const trimmedText = text.trim();
  const attachmentText = urls.length > 0 ? urls.join('\n') : '';

  const messageBody = [trimmedText, attachmentText].filter(Boolean).join('\n\n');

  if (!messageBody) return '';

  return prefix ? `${prefix} ${messageBody}` : messageBody;
};
